import { EventIvStatusUpdated } from '@revenuewell/logic-wrapper-proxy';
import { useNotifications } from '@revenuewell/uc-notifications-client';
import { InsuranceVerificationUpdate } from '@revenuewell/uc-notifications-client/dist/types/notifications';
import { useInsuranceApi } from 'apps/wrapper/src/hooks/use-insurance-api';
import { AppointmentRequestStatus } from 'apps/wrapper/src/services/appointment/types/appointment-request-status';
import { ApptRequestVerificationSummary } from 'apps/wrapper/src/services/insurance/types/appt-request-verification-summary';
import { PatientVerificationSummary } from 'apps/wrapper/src/services/insurance/types/patient-verification-summary';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../../../hooks/use-event-bus';
import { EventApptRequestIvStatusUpdated } from '../../../../../../types/event-bus';
import { useApptRequestsWidget } from '../use-appt-requests-widget';
import { ApptRequestsIvContext } from './context';

export interface ApptRequestsIvProviderProps {
  children: ReactNode;
}

export const ApptRequestsIvProvider: FC<ApptRequestsIvProviderProps> = (props) => {
  const { listen } = useNotifications();
  const { listen: eventBusListen } = useEventBus();
  const { locationId, route, appointmentRequests } = useApptRequestsWidget();

  const {
    status: ivApiStatus,
    getAppointmentRequestVerificationSummaries,
    getAppointmentRequestVerificationSummary,
    getPatientVerificationSummaries,
    getVerificationSummary
  } = useInsuranceApi();

  const [apptRequestIvSummaries, setApptRequestIvSummaries] = useState<ApptRequestVerificationSummary[]>([]);
  const [patientIvSummaries, setPatientIvSummaries] = useState<PatientVerificationSummary[]>([]);
  const statusesToProcess = [
    AppointmentRequestStatus.New,
    AppointmentRequestStatus.Pending,
    AppointmentRequestStatus.Confirmed,
    AppointmentRequestStatus.Unconfirmed
  ];

  useEffect(() => {
    if (!appointmentRequests || appointmentRequests.length === 0) return;

    (async () => {
      const patientIds = appointmentRequests
        .filter((x) => x.patient?.id && !(x.isDirectlyBooked && x.isNewPatientCreated) && statusesToProcess.includes(x.status))
        .map((x) => x.patient?.id!);

      const unverifiedPatientIds = patientIds.filter((x) => !patientIvSummaries.find((p) => p.patientId === x));

      const apptRequestIds = appointmentRequests
        .filter((x) => (!x.patient?.id || (x.isDirectlyBooked && x.isNewPatientCreated)) && statusesToProcess.includes(x.status))
        .map((x) => x.id);

      const unverifiedApptRequestIds = apptRequestIds.filter(
        (x) => !apptRequestIvSummaries.find((p) => p.appointmentRequestId === x)
      );

      const [patientIvs, apptRequestIvs] = await Promise.all([
        getPatientVerificationSummaries(unverifiedPatientIds, locationId),
        getAppointmentRequestVerificationSummaries(unverifiedApptRequestIds, locationId)
      ]);

      setPatientIvSummaries((prev) => [...prev, ...patientIvs]);
      setApptRequestIvSummaries((prev) => [...prev, ...apptRequestIvs]);
    })();
  }, [appointmentRequests]);

  useEffect(() => {
    const ivUpdateEvent = listen('insurance-verification-update', async (event: any) => {
      const locId = event?.locationId || event?.locationid;

      if (locId != locationId) return;

      const { data } = event as InsuranceVerificationUpdate;

      handleIvStatusUpdate(locId, data.entityId);
    });

    return () => {
      ivUpdateEvent();
    };
  }, [listen]);

  useEffect(() => {
    const patientIvStatusUpdatedEvent = eventBusListen('ivStatusUpdated', async (event) => {
      const { data } = event as EventIvStatusUpdated;

      await updatePatientVerification(data.patientId);
    });

    const apptRequestIvStatusUpdatedEvent = eventBusListen('apptRequestIvStatusUpdated', async (event) => {
      const { data } = event as EventApptRequestIvStatusUpdated;

      await updateApptRequestVerification(data.apptRequestId);
    });

    return () => {
      patientIvStatusUpdatedEvent();
      apptRequestIvStatusUpdatedEvent();
    };
  }, [eventBusListen, route]);

  const handleIvStatusUpdate = async (locId: number, ivRequestId?: string) => {
    if (!ivRequestId) return;

    const summary = await getVerificationSummary(locId, parseInt(ivRequestId));

    if (!summary) return;

    if (summary.idPatient) {
      await updatePatientVerification(summary.idPatient);

      return;
    }

    if (summary.appointmentRequestId) {
      await updateApptRequestVerification(summary.appointmentRequestId);
    }
  };

  const updateApptRequestVerification = async (apptRequestId: number) => {
    const updatedIv = await getAppointmentRequestVerificationSummary(apptRequestId, locationId);

    if (updatedIv) {
      const existingIv = apptRequestIvSummaries.find((v) => v.appointmentRequestId == apptRequestId);

      if (existingIv) {
        setApptRequestIvSummaries((prev) => prev.map((v) => (v.appointmentRequestId == apptRequestId ? updatedIv : v)));
      } else {
        setApptRequestIvSummaries((prev) => {
          const filtered = prev.filter((v) => v.appointmentRequestId !== updatedIv.appointmentRequestId);

          return [...filtered, { ...updatedIv }];
        });
      }
    }
  };

  const updatePatientVerification = async (patientId: number) => {
    const updatedIv = (await getPatientVerificationSummaries([patientId], locationId))[0];

    if (updatedIv) {
      const existingIv = patientIvSummaries.find((v) => v.patientId == patientId);

      if (existingIv) {
        setPatientIvSummaries((prev) => prev.map((v) => (v.patientId == patientId ? updatedIv : v)));
      } else {
        setPatientIvSummaries((prev) => {
          const filtered = prev.filter((v) => v.patientId !== updatedIv.patientId);

          return [...filtered, { ...updatedIv }];
        });
      }
    }
  };

  const contextValue = useMemo(
    () => ({
      apptRequestIvSummaries,
      patientIvSummaries,
      ivStatusesToProcess: statusesToProcess,
      isLoadingIv:
        ivApiStatus.getAppointmentRequestVerificationSummaries.isRunning ||
        ivApiStatus.getPatientVerificationSummaries.isRunning
    }),
    [apptRequestIvSummaries, patientIvSummaries, ivApiStatus]
  );

  return <ApptRequestsIvContext.Provider value={contextValue}>{props.children}</ApptRequestsIvContext.Provider>;
};
